import { Injectable } from '@angular/core';

// Firebase imports
import firebase from 'firebase/app';
import 'firebase/firestore';
import { db } from '../firebase-config';

// Angular Material imports
import { MatDialog } from '@angular/material/dialog';

// RXJS imports
import { from, Observable, of, EMPTY } from 'rxjs';
import { map, tap, switchMap, filter, catchError } from 'rxjs/operators';

// Utility imports
import { LocalService } from '@home/storage/local.service';
import {
  CACHE_EXPIRATION_HOURS_CONSTANTS,
  CACHE_KEY_PREFIX_CONSTANTS,
  ACCEPTED_BY_PLATFORM_WEB_CONSTANTS,
} from './configs/insurance-notification.constants';

// Component import
import { InsuranceNotificationComponent } from './insurance-notification.component';

interface CacheEntryType {
  shouldShow: boolean;
  timestamp: number;
}

interface UserAcceptanceType {
  clientId: string;
  dateAcceptance: firebase.firestore.FieldValue;
  documentNumber: string | null;
  dateCreated: firebase.firestore.Timestamp | null;
  acceptedBy: string;
}

@Injectable({
  providedIn: 'root',
})
export class InsuranceNotificationService {
  constructor(private readonly localService: LocalService) {}

  private getCacheKey(clientId: string): string {
    return `${CACHE_KEY_PREFIX_CONSTANTS}${clientId}`;
  }

  private getFromCache(clientId: string): CacheEntryType | null {
    try {
      const cached = this.localService.getJsonValue(this.getCacheKey(clientId));
      if (!cached) return null;

      const entry: CacheEntryType = typeof cached === 'string' ? JSON.parse(cached) : cached;
      const now = Date.now();
      const expirationTime = entry.timestamp + CACHE_EXPIRATION_HOURS_CONSTANTS * 60 * 60 * 1000;

      if (now > expirationTime) {
        this.clearCache(clientId);
        return null;
      }

      return entry;
    } catch {
      return null;
    }
  }

  private setCache(clientId: string, shouldShow: boolean): void {
    try {
      const entry: CacheEntryType = {
        shouldShow,
        timestamp: Date.now(),
      };
      this.localService.setJsonValue(this.getCacheKey(clientId), entry);
    } catch {}
  }

  private clearCache(clientId: string): void {
    try {
      this.localService.setJsonValue(this.getCacheKey(clientId), null);
    } catch {}
  }

  private openInsurancePopup(clientId: string, dialog: MatDialog): void {
    const dialogRef = dialog.open(InsuranceNotificationComponent, {
      width: '500px',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((accepted) => accepted),
        switchMap(() => this.registerAcceptance(clientId)),
        catchError(() => EMPTY)
      )
      .subscribe();
  }

  public mustShowPopup(clientId: string): Observable<boolean> {
    const cached = this.getFromCache(clientId);
    if (cached !== null) {
      return of(cached.shouldShow);
    }

    const userToNotifyRef = db.collection('usersToNotify').doc(clientId);

    return from(userToNotifyRef.get()).pipe(
      map((docSnap) => docSnap.exists),
      tap((shouldShow) => {
        this.setCache(clientId, shouldShow);
      })
    );
  }

  public registerAcceptance(clientId: string): Observable<void> {
    const userToNotifyRef = db.collection('usersToNotify').doc(clientId);
    const userAcceptanceRef = db.collection('usersAcceptance').doc(clientId);

    return from(userToNotifyRef.get()).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists) {
          return of(void 0);
        }

        const originalDocument = docSnap.data();
        const dateCreated = originalDocument?.dateCreated || null;
        const documentNumber = originalDocument?.documentNumber || null;

        const batch = db.batch();
        batch.delete(userToNotifyRef);

        const userAcceptanceData: UserAcceptanceType = {
          clientId: clientId,
          dateAcceptance: firebase.firestore.FieldValue.serverTimestamp(),
          documentNumber: documentNumber,
          dateCreated: dateCreated,
          acceptedBy: ACCEPTED_BY_PLATFORM_WEB_CONSTANTS,
        };

        batch.set(userAcceptanceRef, userAcceptanceData);

        return from(batch.commit());
      }),
      tap(() => {
        this.clearCache(clientId);
      })
    );
  }

  public checkAndShowInsurancePopup(clientId: string, dialog: MatDialog): void {
    this.mustShowPopup(clientId).subscribe((shouldShow) => {
      if (shouldShow) {
        this.openInsurancePopup(clientId, dialog);
      }
    });
  }
}
